import { Component, Input, OnChanges, SimpleChanges, OnDestroy, effect } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import isEmpty from 'lodash/isEmpty';
import { Observable, BehaviorSubject, combineLatest, Subscription, of } from 'rxjs';
import { debounceTime, switchMap, scan, shareReplay, catchError, map } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { CoreApiService, ApiParams } from '../../core-api.service';
import { GearService } from '../../gears/gear.service';
import { LoggingService } from '../../logging.service';
import { ProjectsService } from '../../projects/projects.service';
import { getContainerType, getContainerProjectId } from '../../shared/container.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Analysis } from '../../shared/models/analysis.model';
import { Container } from '../../shared/models/container.model';
import { Paginated } from '../../shared/models/paginated.model';
import { replayValue } from '../../shared/observable.utils';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { FwIconModule } from '@flywheel-io/vision';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { AnalysisResultsComponent } from '../analysis-results/analysis-results.component';
import { SidebarService } from '../../sidebar.service';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { RoutingHelperService } from 'app/src/routing-helper.service';
import { ProjectsQueryService } from 'app/src/projects/projects-query.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'analysis-list',
  templateUrl: './analysis-list.component.html',
  styleUrls: ['./analysis-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    FwIconModule,
    MaterialModule,
    MatTabsModule,
    PipesModule,
    ReactiveFormsModule,
    AnalysisResultsComponent,
  ],
})
export class AnalysisListComponent implements OnChanges, OnDestroy {
  @Input() container: Container
  @Input() containerType: ContainerType | ContainerType[keyof ContainerType]
  @Input() pageSize = 50

  public labelFilter = new FormControl()
  public levelFilter = new FormControl([])
  public analyses$: Observable<Analysis[]>
  public levels: string[]
  public total = 0
  public canRunGear = true

  // readonly projectId = signal<string>(this.routingHelper.getGlobalRouteParams().projectId);
  readonly projectId = toSignal(
    // project id should be in parent route, fall back to activated so it doesn't fail when missing
    (this.activatedRoute.parent || this.activatedRoute).params.pipe(
      map(params => params['projectId'])
    ),
    { requireSync: true }
  );

  readonly projectQuery = injectQuery(() => this.projectQueryService.getProject(this.projectId));

  projectChanged = effect(() => {
    const project = this.projectQuery.data();
    // Only set project container if we don't already have a container (e.g., session, subject)
    // This prevents overriding session/subject containers when viewing their analyses
    if (project && !this.container) {
      this.container = project;
      this.containerType = 'project';
      this.levels = []; // no options to drill down from project
      this.levelFilter.reset(['project']);
      this.initStreams();
      this.updatePermissions();
    }
  });


  private page$: BehaviorSubject<number>
  private subscriptions = {
    searchLabel: Subscription.EMPTY,
    project: Subscription.EMPTY,
  }

  constructor(
    private config: ConfigService,
    private coreApi: CoreApiService,
    private gearService: GearService,
    private logger: LoggingService,
    private projectsService: ProjectsService,
    private activatedRoute: ActivatedRoute,
    private routingHelper: RoutingHelperService,
    private sidebar: SidebarService,
    private projectQueryService: ProjectsQueryService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.containerType) { // Only run if else block if containerType changes
      if (this.containerType === ContainerType.Session) {
        this.levels = ['session', 'acquisition'];
        this.levelFilter.reset(['session', 'acquisition']);
      } else if (this.containerType === ContainerType.Project) {
        this.levels = []; // no options to drill down from project
        this.levelFilter.reset(['project']);
      } else {
        this.levels = ['subject', 'session', 'acquisition'];
        this.levelFilter.reset(['subject']);
      }
    }
    if (changes.container && !isEmpty(this.container)) {
      this.initStreams();
      this.updatePermissions();
      this.subscriptions.searchLabel.unsubscribe();
      this.subscriptions.searchLabel = this.sidebar.search.analysisLabel$.subscribe(analysisLabel => {
        this.labelFilter.setValue(analysisLabel || '');
      });
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  private initStreams(): void {
    this.page$ = new BehaviorSubject(1);
    this.analyses$ = combineLatest([
      this.page$,
      replayValue(this.labelFilter).pipe(debounceTime(200)),
      replayValue(this.levelFilter).pipe(debounceTime(200)),
    ]).pipe(
      switchMap(([page, labelFilter, levelFilter]) => {
        const containerType = getContainerType(this.container);
        const params: ApiParams = {
          sort: 'created:desc',
          limit: this.pageSize,
          page: page,
          inflate_job: true,
          join_avatars: true,
        };
        const filters = [];
        if (containerType && this.container._id) {
          filters.push(`parents.${containerType}=${this.container._id}`);
        }
        if (labelFilter) {
          filters.push(`label=~${labelFilter}`);
        }
        if (levelFilter.length > 0) {
          filters.push(`parent.type=~${levelFilter.join('|')}`);
        }
        if (filters.length > 0) {
          params.filter = filters.join(',');
        }
        return this.coreApi.analyses.query(params)
          .pipe(map(response => (
            { page, response }
          )));
      }),
      scan((results, { page, response }: { page: number, response: Paginated<Analysis[]> }) => {
        this.total = response.total;
        if (page > 1) {
          return [...results, ...response.results];
        }
        return response.results;
      }, [] as Analysis[]),
      catchError(error => {
        this.logger.error(error);
        return of([]);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  refreshAnalyses(): void {
    this.initStreams();
  }

  nextPage(): void {
    this.page$.next(this.page$.value + 1);
  }

  runAnalysisGear(): void {
    this.gearService.runGear({
      containers: [this.container],
      context: 'analysis',
      contextOptions: { analysis: true, data: false },
    }).then(() => {
      this.refreshAnalyses();
    });
  }

  updatePermissions(): void {
    this.projectsService.getProjectPermissions(getContainerProjectId(this.container)).subscribe(permissions => {
      this.canRunGear = permissions.includes(
        this.config.isFeatureEnabled('job_prioritization')
          ? PermissionAction.JobsManage
          : PermissionAction.JobsRunCancel
      );
    });
  }
}
